/* HALO cart drawer: AJAX Cart API, product cards, 1 → 2 HALO upgrade, sticky checkout.
   Opens on any [data-wly-cart-open] link and on the 'wly:cart:changed' event that the
   product page and the home spotlight dispatch after adding to cart. */
if (!customElements.get('halo-cart-drawer')) {
  class HaloCartDrawer extends HTMLElement {
    connectedCallback() {
      const cfg = window.haloCart || {};
      this.s = cfg.strings || {};
      this.fmt = this.parseSample(cfg.sample || '');
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.panel = this.querySelector('.hcd__panel');
      this.itemsEl = this.querySelector('[data-hc-items]');
      this.emptyEl = this.querySelector('[data-hc-empty]');
      this.footEl = this.querySelector('[data-hc-foot]');
      this.shipEl = this.querySelector('[data-hc-ship]');
      this.upEl = this.querySelector('[data-hc-up]');
      this.totalEl = this.querySelector('[data-hc-total]');
      this.totalBtn = this.querySelector('[data-hc-total-btn]');
      this.savingEl = this.querySelector('[data-hc-saving]');
      this.countEl = this.querySelector('[data-hc-count]');
      this.errorEl = this.querySelector('[data-hc-error]');
      this.busyEl = this.querySelector('[data-hc-busy]');
      this.statusEl = this.querySelector('[data-hc-status]');
      this.products = {};
      this.cart = null;

      this.querySelectorAll('[data-hc-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      document.addEventListener('click', (e) => {
        const t = e.target.closest && e.target.closest('[data-wly-cart-open]');
        if (!t) return;
        e.preventDefault();
        this.refresh().then(() => this.open());
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.hidden) this.close(); });
      this.itemsEl.addEventListener('click', (e) => this.onItemClick(e));
      this.upEl.addEventListener('click', (e) => { if (e.target.closest('[data-hc-upgrade]')) this.upgrade(); });
      document.addEventListener('wly:cart:changed', (e) => {
        this.refresh().then(() => { if (!e.detail || e.detail.open !== false) this.open(); });
      });
      this.refresh();
    }

    /* ---------- money: same format as the Liquid money filter on this page ---------- */
    parseSample(sample) {
      const m = sample.match(/^([^\d]*)([\d.,\s  ']+?)([^\d]*)$/);
      if (!m) return { pre: '', post: '', dec: '.', thou: ',', decimals: 2 };
      const num = m[2];
      const lastSep = num.search(/[.,](\d{2})$/);
      const dec = lastSep >= 0 ? num.charAt(lastSep) : '';
      const intPart = lastSep >= 0 ? num.slice(0, lastSep) : num;
      const thouMatch = intPart.match(/\d([^\d])\d{3}$/);
      return { pre: m[1], post: m[3], dec: dec || '.', thou: thouMatch ? thouMatch[1] : '', decimals: dec ? 2 : 0 };
    }
    money(cents) {
      const f = this.fmt;
      const value = (cents / 100).toFixed(f.decimals);
      let [i, d] = value.split('.');
      i = i.replace(/\B(?=(\d{3})+(?!\d))/g, f.thou);
      return f.pre + i + (d ? f.dec + d : '') + f.post;
    }
    t(key, rep) {
      let out = this.s[key] || '';
      Object.keys(rep || {}).forEach((k) => { out = out.split('[[' + k + ']]').join(rep[k]); });
      return out;
    }
    esc(v) { const d = document.createElement('div'); d.textContent = v == null ? '' : String(v); return d.innerHTML; }

    /* ---------- open / close ---------- */
    open() {
      if (!this.hidden) return;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('hc-lock');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const btn = this.querySelector('.hcd__close');
        if (btn) btn.focus({ preventScroll: true });
      });
    }
    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('hc-lock');
      setTimeout(() => { this.hidden = true; if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus(); }, 260);
    }

    /* ---------- data ---------- */
    setBusy(b) { this.busyEl.hidden = !b; this.classList.toggle('is-busy', b); }
    showError(msg) { this.errorEl.textContent = msg || ''; this.errorEl.hidden = !msg; }
    async post(path, body) {
      this.setBusy(true); this.showError('');
      try {
        const r = await fetch(this.root + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        const data = await r.json();
        if (!r.ok) throw new Error(data.description || data.message || this.t('error'));
        return data;
      } catch (err) { this.showError(err.message || this.t('error')); return null; }
      finally { this.setBusy(false); }
    }
    async refresh() {
      try {
        const r = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
        this.cart = await r.json();
        await this.loadProducts();
        this.render();
      } catch (e) { this.showError(this.t('error')); }
    }
    async loadProducts() {
      const handles = [...new Set((this.cart.items || []).map((i) => i.handle))].filter((h) => !this.products[h]);
      await Promise.all(handles.map(async (h) => {
        try { const r = await fetch(this.root + 'products/' + h + '.js'); if (r.ok) this.products[h] = await r.json(); } catch (e) {}
      }));
    }
    variantOf(item) {
      const p = this.products[item.handle];
      return p ? p.variants.find((v) => v.id === item.variant_id) : null;
    }

    /* ---------- render ---------- */
    render() {
      const c = this.cart;
      const empty = c.item_count === 0;
      this.emptyEl.hidden = !empty;
      this.footEl.hidden = empty;
      this.shipEl.hidden = empty;
      this.itemsEl.hidden = empty;
      this.countEl.textContent = empty ? '' : '(' + c.item_count + ')';

      let saving = 0;
      this.itemsEl.innerHTML = c.items.map((item, i) => {
        const v = this.variantOf(item);
        const cmp = v && v.compare_at_price > v.price ? v.compare_at_price * item.quantity : 0;
        if (cmp) saving += cmp - item.final_line_price;
        return this.lineHTML(item, i + 1, cmp);
      }).join('');
      saving += c.total_discount || 0;

      this.totalEl.textContent = this.money(c.total_price);
      this.totalBtn.textContent = this.money(c.total_price);
      this.savingEl.hidden = saving <= 0;
      if (saving > 0) this.savingEl.textContent = this.t('save', { amount: this.money(saving) });
      this.renderUpgrade();
      this.updateHeaderCount(c.item_count);
    }

    lineHTML(item, line, cmp) {
      const img = item.image ? `<img src="${item.image}${item.image.indexOf('?') > -1 ? '&' : '?'}width=240" alt="" width="84" height="84" loading="lazy">` : '';
      const opts = item.product_has_only_default_variant ? '' : (item.options_with_values || []).map((o) => this.esc(o.value)).join(' · ');
      const props = Object.keys(item.properties || {}).filter((k) => k.charAt(0) !== '_' && item.properties[k])
        .map((k) => `<p class="hcd__item-prop">${this.esc(k)} : ${this.esc(item.properties[k])}</p>`).join('');
      const was = cmp > item.final_line_price ? `<s>${this.money(cmp)}</s>` : '';
      return `
        <li class="hcd__item" data-line="${line}">
          <a class="hcd__item-img" href="${item.url}" tabindex="-1" aria-hidden="true">${img}</a>
          <div class="hcd__item-info">
            <div class="hcd__item-top">
              <a class="hcd__item-name" href="${item.url}">${this.esc(item.product_title)}</a>
              <button type="button" class="hcd__remove" data-act="remove" aria-label="${this.esc(this.t('remove', { title: item.product_title }))}">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
            ${opts ? `<p class="hcd__item-opts">${opts}</p>` : ''}
            ${props}
            <div class="hcd__item-row">
              <div class="hcd__qty">
                <button type="button" data-act="dec" aria-label="${this.esc(this.t('decrease'))}">&minus;</button>
                <span aria-live="polite">${item.quantity}</span>
                <button type="button" data-act="inc" aria-label="${this.esc(this.t('increase'))}">+</button>
              </div>
              <p class="hcd__item-price">${was}<b>${this.money(item.final_line_price)}</b></p>
            </div>
          </div>
        </li>`;
    }

    /* One relevant upgrade only: a single HALO in the cart becomes the 2-pack of the same colour. */
    findUpgrade() {
      for (let i = 0; i < this.cart.items.length; i++) {
        const item = this.cart.items[i];
        const p = this.products[item.handle];
        if (!p || item.quantity !== 1) continue;
        const packIdx = (p.options || []).findIndex((o) => /pack/i.test(typeof o === 'string' ? o : o.name));
        if (packIdx < 0) continue;
        const v = p.variants.find((x) => x.id === item.variant_id);
        if (!v) continue;
        const two = p.variants.find((x) => x.id !== v.id && x.available && x.options.every((val, k) => (k === packIdx ? val !== v.options[k] : val === v.options[k])));
        if (!two || two.price <= v.price) continue;
        const colourIdx = packIdx === 0 ? 1 : 0;
        return { line: i + 1, item, from: v, to: two, colour: v.options[colourIdx] };
      }
      return null;
    }
    renderUpgrade() {
      const up = this.findUpgrade();
      this.up = up;
      this.upEl.hidden = !up;
      if (!up) { this.upEl.innerHTML = ''; return; }
      const img = up.item.image ? `<img src="${up.item.image}${up.item.image.indexOf('?') > -1 ? '&' : '?'}width=160" alt="" width="56" height="56" loading="lazy">` : '';
      this.upEl.innerHTML = `
        <div class="hcd__up-media">${img}<span class="hcd__up-badge">×2</span></div>
        <div class="hcd__up-info">
          <p class="hcd__up-title">${this.esc(this.t('upTitle'))}</p>
          <p class="hcd__up-text">${this.esc(this.t('upText', { diff: this.money(up.to.price - up.from.price), each: this.money(Math.round(up.to.price / 2)) }))}</p>
          <p class="hcd__up-sub">${this.esc(this.t('upSub'))}</p>
        </div>
        <button type="button" class="hcd__up-btn" data-hc-upgrade>${this.esc(this.t('upBtn'))}</button>`;
    }

    updateHeaderCount(n) {
      document.querySelectorAll('[data-wly-cart-count]').forEach((el) => { el.textContent = n; el.classList.toggle('is-empty', n === 0); });
      document.querySelectorAll('[data-wly-cart-count-text]').forEach((el) => {
        el.textContent = n === 1 ? this.t('countOne') : this.t('countOther', { n: n });
      });
    }

    /* ---------- actions ---------- */
    async onItemClick(e) {
      const b = e.target.closest('[data-act]');
      if (!b) return;
      const li = b.closest('[data-line]');
      const line = parseInt(li.dataset.line, 10);
      const item = this.cart.items[line - 1];
      if (!item) return;
      let q = item.quantity;
      if (b.dataset.act === 'inc') q += 1;
      else if (b.dataset.act === 'dec') q = Math.max(0, q - 1);
      else q = 0;
      const data = await this.post('cart/change.js', { line, quantity: q });
      if (data) { this.cart = data; await this.loadProducts(); this.render(); }
    }
    async upgrade() {
      const up = this.up;
      if (!up) return;
      const props = {};
      props[this.t('coloursKey')] = up.colour + ' + ' + up.colour;
      const added = await this.post('cart/add.js', { items: [{ id: up.to.id, quantity: 1, properties: props }] });
      if (!added) return;
      const r = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await r.json();
      const idx = cart.items.findIndex((i) => i.variant_id === up.from.id && i.quantity === 1);
      if (idx > -1) await this.post('cart/change.js', { line: idx + 1, quantity: 0 });
      await this.refresh();
    }
  }
  customElements.define('halo-cart-drawer', HaloCartDrawer);
}
